import React, { useState, useEffect } from "react";
import api from "@/lib/api";

/**
 * SettingsPanel — owner-only control surface for things that previously needed
 * voice or direct env-file editing:
 *   • Custom assistant name (what you call her)
 *   • Wake-song MP3 upload (your own Clash clip, plays on first-of-day wake)
 *   • Paste-key UI for the 4 external sister apps (URL + bearer token)
 *   • TTS pacing sliders (stability / style)
 *   • Wipe demo data button
 */
export default function SettingsPanel({ dismiss, initialTab = "name" }) {
  const [tab, setTab] = useState(initialTab);
  return (
    <div className="stage-in rc-panel relative p-5 max-w-3xl w-full" data-testid="stage-settings">
      {dismiss && (
        <button onClick={dismiss} className="absolute top-2 right-2 text-[var(--rc-gold)] hover:rc-glow-text text-lg" data-testid="dismiss-settings">×</button>
      )}
      <div className="font-display tracking-widest text-[var(--rc-gold)] rc-glow-text uppercase text-sm mb-3">
        // SETTINGS
      </div>
      <div className="flex gap-1 mb-4 border-b border-[var(--rc-gold-dim)] flex-wrap">
        {[
          ["name", "NAME"], ["wake", "WAKE SONG"], ["widgets", "WIDGETS"], ["apps", "EXTERNAL APPS"],
          ["voices", "AGENT VOICES"], ["tts", "VOICE PACING"], ["keys", "KEYS & SETUP"], ["diag", "DIAGNOSTICS"], ["danger", "DANGER ZONE"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`settings-tab-${k}`}
            className={`px-3 py-1.5 text-[10px] tracking-widest transition-all ${tab === k ? "text-[var(--rc-gold)] border-b-2 border-[var(--rc-gold)] rc-glow-text" : "text-[var(--rc-text-dim)] hover:text-[var(--rc-gold)]"}`}
          >{l}</button>
        ))}
      </div>
      {tab === "name" && <NameSetting />}
      {tab === "wake" && <WakeSongSetting />}
      {tab === "widgets" && <WidgetsSetting />}
      {tab === "apps" && <ExternalAppsSetting />}
      {tab === "voices" && <AgentVoicesSetting />}
      {tab === "tts" && <TtsSetting />}
      {tab === "keys" && <KeysSetting />}
      {tab === "diag" && <DiagnosticsTab />}
      {tab === "danger" && <DangerZone />}
    </div>
  );
}

/* ---------- Assistant name ---------- */
function NameSetting() {
  const [name, setName] = useState("");
  const [current, setCurrent] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/jarvis/assistant_name").then(r => { setCurrent(r.data.name); setName(r.data.name); }); }, []);
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/jarvis/assistant_name", { name });
      setCurrent(r.data.name);
    } finally { setSaving(false); }
  };
  return (
    <div>
      <div className="rc-label text-[10px] mb-2">CURRENT NAME</div>
      <div className="font-display text-xl rc-glow-text mb-4">{current || "—"}</div>
      <div className="rc-label text-[10px] mb-2">CHANGE TO</div>
      <input
        type="text"
        value={name}
        maxLength={40}
        onChange={(e) => setName(e.target.value)}
        className="w-full bg-transparent border border-[var(--rc-gold-dim)] focus:border-[var(--rc-gold)] outline-none px-3 py-2 text-sm font-display"
        data-testid="settings-name-input"
        placeholder="Raw Company"
      />
      <button onClick={save} disabled={saving || !name || name === current} className="mt-3 rc-btn !text-[10px] !py-1.5" data-testid="settings-name-save">
        {saving ? "SAVING…" : "SAVE"}
      </button>
      <p className="text-[10px] opacity-60 mt-3">She'll respond to whatever you call her — say "call yourself X" or set it here.</p>
    </div>
  );
}

/* ---------- Wake song ---------- */
function WakeSongSetting() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const fetchStatus = async () => {
    try {
      // Use the lightweight 200-always status probe so the panel mount
      // doesn't spit a 404 in console when no wake song is uploaded.
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jarvis/wake_song_status`);
      const j = await r.json().catch(() => ({ exists: false }));
      setInfo({ uploaded: !!j.exists, ...j });
    } catch { setInfo({ uploaded: false }); }
  };
  useEffect(() => { fetchStatus(); }, []);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { setMsg("File too large — keep under 100 MB."); return; }
    setBusy(true); setMsg("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/jarvis/upload_wake_song", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const extracted = r.data.extracted_from_video ? " (audio extracted from video)" : "";
      setMsg(`Saved ${r.data.filename} (${(r.data.size / 1024).toFixed(0)} KB)${extracted}`);
      fetchStatus();
    } catch (e) {
      setMsg(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete uploaded wake song?")) return;
    await api.delete("/jarvis/wake_song");
    setMsg("Removed."); fetchStatus();
  };
  return (
    <div>
      <div className="rc-label text-[10px] mb-2">CURRENT</div>
      <div className="font-display text-base mb-3">
        {info?.uploaded ? <span className="rc-glow-text">CUSTOM MP3 UPLOADED ✓</span> : <span className="opacity-60">DEFAULT SYNTH STINGER (8s)</span>}
      </div>
      <label className="block border border-dashed border-[var(--rc-gold-dim)] hover:border-[var(--rc-gold)] p-6 text-center cursor-pointer transition-all" data-testid="wake-upload-zone">
        <input type="file" accept="audio/*,video/*" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid="wake-upload-input" />
        <div className="rc-label text-[10px] opacity-70">CLICK TO UPLOAD MP3 / WAV / M4A / MP4 / MOV</div>
        <div className="text-[10px] opacity-50 mt-1">≤ 100 MB · screen recordings auto-extracted to MP3 · plays 10s on first wake of the day</div>
      </label>
      {busy && <div className="text-[10px] mt-2 opacity-70">Uploading…</div>}
      {msg && <div className="text-[10px] mt-2 text-[var(--rc-gold)]" data-testid="wake-upload-msg">{msg}</div>}
      {info?.uploaded && (
        <button onClick={remove} className="mt-3 text-[10px] tracking-widest text-red-400 hover:underline" data-testid="wake-remove">REMOVE UPLOADED SONG</button>
      )}
    </div>
  );
}

/* ---------- External apps paste-key ---------- */
function ExternalAppsSetting() {
  const [apps, setApps] = useState([]);
  const [form, setForm] = useState({ name: "", base_url: "", api_key: "", stats_path: "/api/stats" });
  const [busy, setBusy] = useState(false);
  const fetchAll = async () => {
    const r = await api.get("/external_apps");
    setApps(r.data || []);
  };
  useEffect(() => { fetchAll(); }, []);
  const add = async () => {
    if (!form.name || !form.base_url) return;
    setBusy(true);
    try {
      await api.post("/external_apps", form);
      setForm({ name: "", base_url: "", api_key: "", stats_path: "/api/stats" });
      fetchAll();
    } finally { setBusy(false); }
  };
  const remove = async (id) => {
    await api.delete(`/external_apps/${id}`);
    fetchAll();
  };
  const sync = async (id) => {
    setBusy(true);
    try { await api.post(`/external_apps/${id}/sync`); fetchAll(); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <div className="rc-label text-[10px] mb-2">REGISTERED APPS</div>
      <div className="space-y-2 mb-4">
        {apps.length === 0 && <div className="text-[10px] opacity-60">None registered yet. Add one below.</div>}
        {apps.map((a) => (
          <div key={a.id} className="border border-[var(--rc-gold-dim)] p-2 flex justify-between items-center text-xs" data-testid={`ext-app-row-${a.name}`}>
            <div>
              <div className="font-display rc-glow-text">{a.name}</div>
              <div className="text-[10px] opacity-60">{a.base_url}{a.stats_path}</div>
              {a.last_synced && <div className="text-[10px] opacity-50">synced {a.last_synced.slice(0, 16).replace("T", " ")}</div>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => sync(a.id)} disabled={busy} className="rc-btn !text-[9px] !py-1 !px-2" data-testid={`ext-app-sync-${a.name}`}>SYNC</button>
              <button onClick={() => remove(a.id)} className="text-[10px] text-red-400 hover:underline" data-testid={`ext-app-remove-${a.name}`}>×</button>
            </div>
          </div>
        ))}
      </div>
      <div className="rc-label text-[10px] mb-2">ADD NEW APP</div>
      <div className="grid grid-cols-2 gap-2">
        {[["name", "snapdog"], ["base_url", "https://snapdog.co.uk"], ["api_key", "bearer token (optional)"], ["stats_path", "/api/stats"]].map(([k, ph]) => (
          <input
            key={k}
            type={k === "api_key" ? "password" : "text"}
            value={form[k]}
            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            placeholder={ph}
            data-testid={`ext-app-${k}`}
            className="bg-transparent border border-[var(--rc-gold-dim)] focus:border-[var(--rc-gold)] outline-none px-2 py-1.5 text-xs font-display"
          />
        ))}
      </div>
      <button onClick={add} disabled={busy || !form.name || !form.base_url} className="mt-3 rc-btn !text-[10px] !py-1.5" data-testid="ext-app-add">
        ADD APP
      </button>
    </div>
  );
}

/* ---------- Widgets management ---------- */
const WIDGET_GALLERY = [
  { kind: "clock",          label: "Clock",           emoji: "🕒", needs: [] },
  { kind: "date",           label: "Today's Date",    emoji: "📅", needs: [] },
  { kind: "weather",        label: "Weather",         emoji: "☀",  needs: [] },
  { kind: "btc_ticker",     label: "BTC Price",       emoji: "₿",  needs: [] },
  { kind: "eth_ticker",     label: "ETH Price",       emoji: "Ξ",  needs: [] },
  { kind: "gold_ticker",    label: "Gold Price",      emoji: "🥇", needs: [] },
  { kind: "stock_ticker",   label: "Stock Ticker",    emoji: "📈", needs: ["symbol"] },
  { kind: "live_users",     label: "Live Users",      emoji: "👥", needs: ["app_key"] },
  { kind: "dashboard_iframe", label: "Embedded Dashboard", emoji: "🖥", needs: ["url", "title"] },
  { kind: "custom_text",    label: "Custom Text",     emoji: "✎",  needs: ["text"] },
  { kind: "quote",          label: "Quote",           emoji: "❝",  needs: ["text"] },
  { kind: "logo_image",     label: "Logo / Image",    emoji: "🖼", needs: ["url"] },
  { kind: "illuminati_eye", label: "All-Seeing Eye",  emoji: "👁", needs: [] },
  { kind: "moon_phase",     label: "Moon Phase",      emoji: "🌙", needs: [] },
];
const POSITIONS = ["top_left", "top_center", "top_right", "bottom_left", "bottom_center", "bottom_right"];
const SIZES = ["small", "medium", "large"];
const APP_KEYS = ["all", "snapdog", "rawcompany", "clothing", "trader"];

function WidgetsSetting() {
  const [widgets, setWidgets] = useState([]);
  const [picker, setPicker] = useState(null); // { kind, label, needs, ...form }
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const r = await api.get("/widgets");
    setWidgets(r.data || []);
  };
  useEffect(() => { refresh(); }, []);

  const add = async (form) => {
    setBusy(true);
    try {
      await api.post("/widgets", form);
      window.dispatchEvent(new Event("rawco-widgets-changed"));
      setPicker(null);
      refresh();
    } catch (e) {
      window.alert(`Add failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/widgets/${id}`);
    window.dispatchEvent(new Event("rawco-widgets-changed"));
    refresh();
  };

  return (
    <div>
      <div className="rc-label text-[10px] mb-2">ACTIVE WIDGETS</div>
      {widgets.length === 0 && <div className="text-[10px] opacity-60 mb-4">None pinned yet. Pick one below or just say "add a clock to the top right".</div>}
      <div className="space-y-1.5 mb-5">
        {widgets.map((w) => {
          const meta = WIDGET_GALLERY.find(g => g.kind === w.kind);
          const detail = w.app_key || w.symbol || w.title || w.text || w.url || "";
          return (
            <div key={w.id} className="border border-[var(--rc-gold-dim)] p-2 flex justify-between items-center text-xs" data-testid={`widget-row-${w.kind}`}>
              <div>
                <div className="font-display rc-glow-text">
                  {meta?.emoji || "•"} {meta?.label || w.kind}
                  {detail && <span className="opacity-60 ml-2 text-[10px]">{detail}</span>}
                </div>
                <div className="text-[10px] opacity-60">{w.position} · {w.size}</div>
              </div>
              <button onClick={() => remove(w.id)} className="text-[10px] text-red-400 hover:underline" data-testid={`widget-row-remove-${w.kind}`}>REMOVE</button>
            </div>
          );
        })}
      </div>

      <div className="rc-label text-[10px] mb-2">+ ADD WIDGET</div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {WIDGET_GALLERY.map((g) => (
          <button
            key={g.kind}
            onClick={() => setPicker({ ...g, position: "top_right", size: "medium", symbol: "", text: "", url: "", title: "", app_key: "all" })}
            className="border border-[var(--rc-gold-dim)] hover:border-[var(--rc-gold)] p-2 text-left transition-all"
            data-testid={`widget-add-${g.kind}`}
          >
            <div className="font-display text-xs rc-glow-text">{g.emoji} {g.label}</div>
            <div className="text-[9px] opacity-50 mt-0.5">{g.kind}</div>
          </button>
        ))}
      </div>

      {picker && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" style={{ background: "rgba(0,4,12,0.85)", backdropFilter: "blur(8px)" }} onClick={() => setPicker(null)}>
          <div className="rc-panel p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="widget-config-modal">
            <div className="font-display rc-glow-text text-sm mb-3">// {picker.emoji} {picker.label.toUpperCase()}</div>

            {picker.needs.includes("symbol") && (
              <Field label="STOCK SYMBOL (e.g. TSLA, AAPL)">
                <input type="text" value={picker.symbol} onChange={(e) => setPicker({ ...picker, symbol: e.target.value.toUpperCase() })}
                  className="w-full bg-transparent border border-[var(--rc-gold-dim)] focus:border-[var(--rc-gold)] outline-none px-2 py-1 text-xs"
                  data-testid="widget-cfg-symbol" />
              </Field>
            )}
            {picker.needs.includes("app_key") && (
              <Field label="SISTER APP">
                <select value={picker.app_key} onChange={(e) => setPicker({ ...picker, app_key: e.target.value })}
                  className="w-full bg-black border border-[var(--rc-gold-dim)] focus:border-[var(--rc-gold)] outline-none px-2 py-1 text-xs"
                  data-testid="widget-cfg-appkey">
                  {APP_KEYS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </Field>
            )}
            {picker.needs.includes("url") && (
              <Field label="URL">
                <input type="text" value={picker.url} onChange={(e) => setPicker({ ...picker, url: e.target.value })}
                  placeholder="https://..." className="w-full bg-transparent border border-[var(--rc-gold-dim)] focus:border-[var(--rc-gold)] outline-none px-2 py-1 text-xs"
                  data-testid="widget-cfg-url" />
              </Field>
            )}
            {picker.needs.includes("title") && (
              <Field label="LABEL (shown above the iframe)">
                <input type="text" value={picker.title} onChange={(e) => setPicker({ ...picker, title: e.target.value })}
                  placeholder="SnapDog Admin" className="w-full bg-transparent border border-[var(--rc-gold-dim)] focus:border-[var(--rc-gold)] outline-none px-2 py-1 text-xs"
                  data-testid="widget-cfg-title" />
              </Field>
            )}
            {picker.needs.includes("text") && (
              <Field label="TEXT">
                <input type="text" value={picker.text} onChange={(e) => setPicker({ ...picker, text: e.target.value })}
                  placeholder="FOCUS" className="w-full bg-transparent border border-[var(--rc-gold-dim)] focus:border-[var(--rc-gold)] outline-none px-2 py-1 text-xs"
                  data-testid="widget-cfg-text" />
              </Field>
            )}
            <Field label="POSITION">
              <select value={picker.position} onChange={(e) => setPicker({ ...picker, position: e.target.value })}
                className="w-full bg-black border border-[var(--rc-gold-dim)] focus:border-[var(--rc-gold)] outline-none px-2 py-1 text-xs"
                data-testid="widget-cfg-position">
                {POSITIONS.map(p => <option key={p} value={p}>{p.replace("_", " ")}</option>)}
              </select>
            </Field>
            <Field label="SIZE">
              <select value={picker.size} onChange={(e) => setPicker({ ...picker, size: e.target.value })}
                className="w-full bg-black border border-[var(--rc-gold-dim)] focus:border-[var(--rc-gold)] outline-none px-2 py-1 text-xs"
                data-testid="widget-cfg-size">
                {SIZES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setPicker(null)} className="flex-1 border border-[var(--rc-gold-dim)] py-2 text-[10px] tracking-widest opacity-70 hover:opacity-100" data-testid="widget-cfg-cancel">CANCEL</button>
              <button
                onClick={() => {
                  const payload = { kind: picker.kind, position: picker.position, size: picker.size };
                  picker.needs.forEach(n => { if (picker[n]) payload[n] = picker[n]; });
                  add(payload);
                }}
                disabled={busy || picker.needs.some(n => !picker[n])}
                className="flex-1 rc-btn !text-[10px] !py-2"
                data-testid="widget-cfg-add"
              >{busy ? "ADDING…" : "ADD WIDGET"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <div className="rc-label text-[10px] opacity-70 mb-1">{label}</div>
      {children}
    </div>
  );
}

/* ---------- TTS pacing ---------- */
function TtsSetting() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/jarvis/tts_settings").then(r => setS(r.data)); }, []);
  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.post("/jarvis/tts_settings", patch);
      setS(r.data);
    } finally { setSaving(false); }
  };
  if (!s) return <div className="text-xs opacity-60">Loading…</div>;
  const Slider = ({ k, label, hint }) => (
    <div className="mb-4">
      <div className="flex justify-between mb-1">
        <span className="rc-label text-[10px]">{label}</span>
        <span className="text-[10px] tabular-nums opacity-70">{s[k].toFixed(2)}</span>
      </div>
      <input
        type="range" min="0" max="1" step="0.05"
        value={s[k]}
        onChange={(e) => save({ [k]: parseFloat(e.target.value) })}
        disabled={saving}
        className="w-full accent-[var(--rc-gold)]"
        data-testid={`tts-${k}-slider`}
      />
      <div className="text-[9px] opacity-50 mt-0.5">{hint}</div>
    </div>
  );
  return (
    <div>
      <Slider k="stability" label="STABILITY" hint="↑ = smoother / more monotone · ↓ = more expressive but jittery" />
      <Slider k="similarity_boost" label="VOICE LIKENESS" hint="how closely to mimic the source voice" />
      <Slider k="style" label="STYLE INTENSITY" hint="↑ = more emotional · ↓ = more deliberate" />
    </div>
  );
}

/* ---------- Keys & Setup ---------- */
function KeysSetting() {
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState({}); // {KEY: newValue}
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => {
    const r = await api.get("/jarvis/env");
    setRows(r.data.keys || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (Object.keys(pending).length === 0) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api.post("/jarvis/env", { updates: pending });
      setPending({});
      await load();
      const restart = r.data.needs_restart || [];
      if (restart.length > 0) {
        setMsg({ kind: "warn", text: `Saved. Restart needed for: ${restart.join(", ")}` });
      } else {
        setMsg({ kind: "ok", text: `Saved ${r.data.updated.length} key(s) — active immediately.` });
      }
    } catch (e) {
      setMsg({ kind: "fail", text: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const restart = async () => {
    setBusy(true);
    try {
      await api.post("/jarvis/restart_backend");
      setMsg({ kind: "ok", text: "Backend restarting — will be back in ~5s." });
      setTimeout(load, 6000);
    } catch (e) {
      setMsg({ kind: "fail", text: e.message });
    } finally { setBusy(false); }
  };

  const q = filter.trim().toLowerCase();
  const filtered = q ? rows.filter(r => r.key.toLowerCase().includes(q) || r.label.toLowerCase().includes(q)) : rows;
  const grouped = {};
  filtered.forEach(r => { (grouped[r.service] = grouped[r.service] || []).push(r); });

  return (
    <div>
      <div className="flex justify-between items-center mb-3 gap-2">
        <input
          type="text"
          placeholder="filter…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="flex-1 bg-transparent border border-[var(--rc-gold-dim)] focus:border-[var(--rc-gold)] outline-none px-2 py-1 text-xs"
          data-testid="keys-filter"
        />
        <button
          onClick={save}
          disabled={busy || Object.keys(pending).length === 0}
          className="rc-btn !text-[10px] !py-1"
          data-testid="keys-save"
        >{busy ? "SAVING…" : `SAVE ${Object.keys(pending).length || ""}`}</button>
        <button onClick={restart} disabled={busy} className="rc-btn !text-[10px] !py-1" data-testid="keys-restart">
          RESTART
        </button>
      </div>
      {msg && (
        <div
          className={`mb-3 px-3 py-2 text-[11px] border ${
            msg.kind === "ok" ? "border-green-500/60 bg-green-500/10 text-green-300" :
            msg.kind === "warn" ? "border-orange-500/60 bg-orange-500/10 text-orange-300" :
            "border-red-500/60 bg-red-500/10 text-red-300"
          }`}
          data-testid="keys-msg"
        >{msg.text}</div>
      )}
      <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
        {Object.entries(grouped).map(([svc, keys]) => (
          <div key={svc}>
            <div className="rc-label text-[9px] mb-1.5 opacity-80">{svc.toUpperCase().replace(/_/g, " ")}</div>
            <div className="space-y-1.5">
              {keys.map((r) => (
                <div key={r.key} className="border border-[var(--rc-gold-dim)] p-2.5" data-testid={`keys-row-${r.key}`}>
                  <div className="flex justify-between items-center mb-1.5">
                    <div className="text-xs font-display">
                      <span className={`inline-block w-2 h-2 rounded-full mr-2 ${r.present ? "bg-green-400 shadow-green-400/50" : "bg-red-500 shadow-red-500/50"} shadow-md`} />
                      {r.label}
                      {r.required && <span className="text-[9px] text-red-400 ml-1">*REQ</span>}
                    </div>
                    <div className="text-[9px] opacity-50">{r.key}</div>
                  </div>
                  <input
                    type={r.sensitive ? "password" : "text"}
                    placeholder={r.present ? (r.preview || "(set — paste to change)") : "(missing — paste here)"}
                    value={pending[r.key] ?? ""}
                    onChange={(e) => setPending({ ...pending, [r.key]: e.target.value })}
                    className="w-full bg-transparent border border-[var(--rc-gold-dim)] focus:border-[var(--rc-gold)] outline-none px-2 py-1 text-xs font-mono"
                    data-testid={`keys-input-${r.key}`}
                  />
                  <div className="text-[10px] opacity-50 mt-1">{r.where}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Diagnostics ---------- */
function DiagnosticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const run = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/jarvis/diagnostics");
      setData(r.data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { run(); /* fire once on mount */ }, []);

  const dot = (status) => {
    const cls = status === "ok" ? "bg-green-400 shadow-green-400/50" :
                status === "warn" ? "bg-orange-400 shadow-orange-400/50" :
                "bg-red-500 shadow-red-500/50";
    return <span className={`inline-block w-2 h-2 rounded-full shadow-md mr-2 ${cls}`} />;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="rc-label text-[10px]">INTEGRATION HEALTH</div>
        <button onClick={run} disabled={loading} className="rc-btn !text-[10px] !py-1" data-testid="diag-rerun">
          {loading ? "PROBING…" : "RE-RUN"}
        </button>
      </div>
      {err && <div className="text-xs text-red-400 mb-2">{err}</div>}
      {data && (
        <div className="space-y-1.5">
          {Object.entries(data.services).map(([key, v]) => (
            <div key={key} className="border border-[var(--rc-gold-dim)] p-2.5" data-testid={`diag-row-${key}`}>
              <div className="flex justify-between items-start">
                <div className="text-xs font-display">{dot(v.status)}{key.replace(/_/g, " ").toUpperCase()}</div>
                <div className="text-[9px] opacity-50 tabular-nums">{v.status?.toUpperCase()}</div>
              </div>
              <div className="text-[10px] opacity-70 mt-1 ml-4">{v.message}</div>
            </div>
          ))}
          <div className="text-[9px] opacity-50 text-right mt-2">
            checked {data.checked_at?.slice(11, 19)} UTC
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Danger zone ---------- */
function DangerZone() {
  const [done, setDone] = useState(null);
  const [issuesMsg, setIssuesMsg] = useState(null);
  const [issuesBusy, setIssuesBusy] = useState(false);
  const [simBusy, setSimBusy] = useState(false);
  const [simMsg, setSimMsg] = useState(null);
  const wipe = async () => {
    if (!window.confirm("Wipe ALL messages, issues, finance, trades, sales, tasks? This cannot be undone.")) return;
    const r = await api.post("/owner/wipe_demo_data");
    setDone(r.data.cleared);
  };
  const wipeSynthetic = async () => {
    if (!window.confirm("Purge auto-generated synthetic issues only? Real issues will be kept.")) return;
    setIssuesBusy(true); setIssuesMsg(null);
    try {
      const r = await api.post("/owner/wipe_synthetic_issues");
      setIssuesMsg(`✓ Removed ${r.data.deleted} synthetic issues. ${r.data.remaining_needs_owner} real issues remain.`);
    } catch (e) {
      setIssuesMsg(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally { setIssuesBusy(false); }
  };
  const wipeAllIssues = async () => {
    if (!window.confirm("🧨 NUCLEAR — Delete EVERY issue (real + synthetic). True zero starting point. Continue?")) return;
    setIssuesBusy(true); setIssuesMsg(null);
    try {
      const r = await api.post("/owner/wipe_all_issues");
      setIssuesMsg(`✓ NUKED. Deleted ${r.data.deleted} issues. Dashboard will read 0.`);
    } catch (e) {
      setIssuesMsg(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally { setIssuesBusy(false); }
  };
  const sim = async (kind, pnl = null) => {
    setSimBusy(true); setSimMsg(null);
    try {
      const body = { symbol: "GOLD", side: "buy", action: kind };
      if (pnl !== null) body.pnl = pnl;
      const r = await api.post("/raw/chat", { message: `simulate trade ${kind} on gold` + (pnl !== null ? ` with pnl ${pnl}` : "") });
      setSimMsg(r.data?.action?.result?.spoken || "Simulated.");
    } catch (e) {
      setSimMsg(`Failed: ${e.message}`);
    } finally { setSimBusy(false); }
  };
  return (
    <div className="space-y-6">
      <div>
        <div className="rc-label text-[10px] mb-2">🧪 TRY A TRADE (SIMULATOR)</div>
        <p className="text-[11px] opacity-70 mb-3">
          Fires fake trade-event alerts through the real alert / Telegram / spoken-alert pipeline
          so you can hear how the auto-scalper voice sounds — without placing a real Capital trade.
        </p>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => sim("open")} disabled={simBusy} className="rc-btn !text-[10px] !py-1.5" data-testid="sim-open">SIM OPEN</button>
          <button onClick={() => sim("close", 35)} disabled={simBusy} className="rc-btn !text-[10px] !py-1.5" data-testid="sim-win">SIM WIN +£35</button>
          <button onClick={() => sim("close", -22)} disabled={simBusy} className="border border-red-500/60 text-red-400 px-3 py-1.5 text-[10px] tracking-widest hover:bg-red-500/10" data-testid="sim-loss">SIM LOSS −£22</button>
          <button onClick={() => sim("close")} disabled={simBusy} className="rc-btn !text-[10px] !py-1.5" data-testid="sim-random">SIM RANDOM</button>
        </div>
        {simMsg && <div className="mt-2 text-[10px] text-[var(--rc-gold)]" data-testid="sim-msg">{simMsg}</div>}
      </div>
      <div className="border-t border-[var(--rc-gold-dim)] pt-4">
        <div className="rc-label text-[10px] mb-2 text-red-400">🧨 WIPE ISSUES (production-safe)</div>
        <p className="text-xs opacity-70 mb-3">
          Your dashboard says "1033 open issues" but they're all auto-generated from the old fake scanner.
          Click <b>Purge Synthetic</b> first — that's the safe option and keeps any real issues. If you
          STILL see leftover garbage, hit <b>Nuke All</b>.
        </p>
        <div className="flex gap-2 flex-wrap">
          <button onClick={wipeSynthetic} disabled={issuesBusy}
            className="border border-amber-500/70 text-amber-400 px-3 py-1.5 text-[10px] tracking-widest hover:bg-amber-500/10"
            data-testid="wipe-synthetic-issues">{issuesBusy ? "WORKING…" : "PURGE SYNTHETIC"}</button>
          <button onClick={wipeAllIssues} disabled={issuesBusy}
            className="border border-red-500/70 text-red-400 px-3 py-1.5 text-[10px] tracking-widest hover:bg-red-500/10"
            data-testid="wipe-all-issues">{issuesBusy ? "WORKING…" : "🧨 NUKE ALL ISSUES"}</button>
        </div>
        {issuesMsg && <div className="mt-2 text-[10px] text-[var(--rc-gold)]" data-testid="wipe-issues-msg">{issuesMsg}</div>}
      </div>
      <div className="border-t border-[var(--rc-gold-dim)] pt-4">
        <div className="rc-label text-[10px] mb-2 text-red-400">⚠ WIPE DEMO DATA</div>
        <p className="text-xs opacity-70 mb-3">
          Nukes all messages, issues, finance entries, trades, sales and tasks from the database.
          Apps, products, notes and reminders are preserved.
        </p>
        <button onClick={wipe} className="border border-red-500/70 text-red-400 px-3 py-1.5 text-[10px] tracking-widest hover:bg-red-500/10" data-testid="wipe-demo">
          WIPE DEMO DATA
        </button>
        {done && <pre className="mt-3 text-[10px] opacity-70" data-testid="wipe-result">{JSON.stringify(done, null, 2)}</pre>}
      </div>
    </div>
  );
}

/* ---------- Agent Voices (per-agent ElevenLabs picker) ----------
   Owner brief (Feb 2026): "no where on the settings page has any button or
   anything to change each voices?" — this tab lists every agent and lets
   the owner pick an ElevenLabs voice, paste a custom voice_id, preview it,
   and save. Persists via PUT /api/jarvis/agents/{id}. */

const AGENT_VOICE_PRESETS = [
  { id: "",                     label: "(default — main Jarvis voice)" },
  { id: "onwK4e9ZLuTAKqWW03F9", label: "Daniel — deep British male" },
  { id: "XB0fDUnXU5powFXDhCwa", label: "Charlotte — warm British female" },
  { id: "AZnzlk1XvdvUeBnXmlld", label: "Domi — energetic female" },
  { id: "ErXwobaYiN019PkySvjV", label: "Antoni — clear factual male" },
  { id: "MF3mGyEYCl7XYWbV9V6O", label: "Elli — playful higher-pitched" },
  { id: "EXAVITQu4vr4xnSDxMaL", label: "Sarah — soft American female" },
  { id: "21m00Tcm4TlvDq8ikWAM", label: "Rachel — calm American female" },
  { id: "pNInz6obpgDQGcFmaJgB", label: "Adam — deep American male" },
  { id: "TxGEqnHWrfWFTfGW9XjX", label: "Josh — confident young male" },
  { id: "yoZ06aMxZJJ28mfd3POQ", label: "Sam — neutral male" },
];

function AgentVoicesSetting() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  // per-agent local edits & save status: { [agentId]: { voice_id, saving, saved, error } }
  const [edits, setEdits] = useState({});
  const [previewing, setPreviewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/jarvis/agents");
      const list = Array.isArray(r.data) ? r.data : (r.data?.agents || []);
      setAgents(list);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setVoice = (id, voice_id) => {
    setEdits(s => ({ ...s, [id]: { ...(s[id] || {}), voice_id, saved: false, error: null } }));
  };

  const save = async (a) => {
    const e = edits[a.id] || {};
    const newVoice = e.voice_id ?? (a.voice_id || "");
    setEdits(s => ({ ...s, [a.id]: { ...e, saving: true, error: null } }));
    try {
      await api.put(`/jarvis/agents/${a.id}`, { ...a, voice_id: newVoice.trim() });
      setEdits(s => ({ ...s, [a.id]: { voice_id: newVoice, saving: false, saved: true, error: null } }));
      // refresh local list and broadcast so AgentsRoster updates
      await load();
      window.dispatchEvent(new Event("rawco-agents-changed"));
      setTimeout(() => setEdits(s => ({ ...s, [a.id]: { ...(s[a.id] || {}), saved: false } })), 2500);
    } catch (err) {
      setEdits(s => ({ ...s, [a.id]: { voice_id: newVoice, saving: false, error: err.response?.data?.detail || err.message } }));
    }
  };

  const preview = async (a) => {
    const e = edits[a.id] || {};
    const vid = (e.voice_id ?? (a.voice_id || "")).trim();
    setPreviewing(a.id);
    try {
      const r = await api.post("/jarvis/tts", {
        text: `Hi, this is ${a.name}. ${a.role || "On the team."}`,
        voice_id: vid || undefined,
      });
      if (r.data?.audio) {
        const audio = new Audio(`data:audio/mpeg;base64,${r.data.audio}`);
        audio.onended = () => setPreviewing(null);
        audio.onerror = () => setPreviewing(null);
        audio.play().catch(() => setPreviewing(null));
      } else {
        // Fallback to browser speechSynthesis if ElevenLabs key is missing
        try {
          const u = new SpeechSynthesisUtterance(`Hi, this is ${a.name}.`);
          u.onend = () => setPreviewing(null);
          window.speechSynthesis.speak(u);
        } catch { setPreviewing(null); }
      }
    } catch {
      setPreviewing(null);
    }
  };

  if (loading) return <div className="text-[11px] opacity-60" data-testid="voices-loading">Loading agents…</div>;
  if (agents.length === 0) {
    return (
      <div className="text-[11px] opacity-70" data-testid="voices-empty">
        No agents found. Build one in Agent Studio (say &quot;open agent studio&quot;) then come back here to pick its voice.
      </div>
    );
  }

  return (
    <div data-testid="voices-tab-content">
      <p className="text-[11px] opacity-70 mb-3">
        Each sub-agent can have its OWN ElevenLabs voice. Pick a preset or paste any custom <code>voice_id</code> from your ElevenLabs library.
        Hit <em>Preview</em> to hear them, <em>Save</em> to lock it in.
      </p>
      <div className="space-y-3">
        {agents.map((a) => {
          const e = edits[a.id] || {};
          const current = (e.voice_id !== undefined ? e.voice_id : (a.voice_id || ""));
          const matchPreset = AGENT_VOICE_PRESETS.find(v => v.id === current);
          const dirty = (e.voice_id !== undefined) && (e.voice_id.trim() !== (a.voice_id || "").trim());
          return (
            <div key={a.id} className="border border-[var(--rc-gold-dim)] p-3" data-testid={`voice-row-${a.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-base" style={{ width: 22, textAlign: "center" }}>{a.icon || "🤖"}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-display tracking-wider rc-glow-text">{(a.name || "Agent").toUpperCase()}</div>
                  <div className="text-[10px] opacity-60 truncate">{a.role || a.purpose || "Sub-agent"}</div>
                </div>
                <button
                  type="button"
                  onClick={() => preview(a)}
                  disabled={previewing === a.id}
                  data-testid={`voice-preview-${a.id}`}
                  className="rc-btn !text-[10px] !py-1 !px-2"
                  title={`Hear ${a.name} say a sample`}
                >{previewing === a.id ? "▶ playing…" : "▶ Preview"}</button>
                <button
                  type="button"
                  onClick={() => save(a)}
                  disabled={!dirty || e.saving}
                  data-testid={`voice-save-${a.id}`}
                  className="rc-btn !text-[10px] !py-1 !px-2"
                  style={{ borderColor: dirty ? "var(--rc-gold)" : undefined }}
                >{e.saving ? "saving…" : e.saved ? "✓ saved" : "Save"}</button>
              </div>

              <label className="block">
                <div className="rc-label text-[9px] opacity-70 mb-1">PRESET</div>
                <select
                  value={matchPreset ? current : "__custom__"}
                  onChange={(ev) => setVoice(a.id, ev.target.value === "__custom__" ? current : ev.target.value)}
                  data-testid={`voice-preset-${a.id}`}
                  className="w-full bg-transparent border border-[var(--rc-gold-dim)] focus:border-[var(--rc-gold)] outline-none px-2 py-1.5 text-[11px] font-display"
                  style={{ color: "var(--rc-text)" }}
                >
                  {AGENT_VOICE_PRESETS.map(v => (
                    <option key={v.id || "default"} value={v.id} style={{ background: "#000" }}>{v.label}</option>
                  ))}
                  {!matchPreset && (
                    <option value="__custom__" style={{ background: "#000" }}>(custom voice_id — see below)</option>
                  )}
                </select>
              </label>

              <label className="block mt-2">
                <div className="rc-label text-[9px] opacity-70 mb-1">CUSTOM VOICE_ID (optional)</div>
                <input
                  type="text"
                  value={current}
                  onChange={(ev) => setVoice(a.id, ev.target.value)}
                  data-testid={`voice-custom-${a.id}`}
                  placeholder="paste any voice_id from your ElevenLabs library"
                  className="w-full bg-transparent border border-[var(--rc-gold-dim)] focus:border-[var(--rc-gold)] outline-none px-2 py-1.5 text-[11px] font-mono"
                />
              </label>

              {e.error && (
                <div className="text-[10px] mt-2" style={{ color: "#f87171" }} data-testid={`voice-error-${a.id}`}>{e.error}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

